/* eslint-disable consistent-return */
/* eslint-disable no-console, no-underscore-dangle */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { isMsalTokenExpiredError, Auth, saveSSOReturnUrl, clearSSOReturnUrl, getSSOReturnUrl, clearMsalStorage } from '@exo/frontend-common-app-shell';
import { SessionTimeoutModal } from './SessionTimeoutModal';
import { attemptSilentTokenRefresh, initializeBaseStoreChangeDetection, readMsalCache, MSAL_CACHE } from './applications-utils';
import { trackEvent } from './gaTagManager';

/**
 * SessionTimeoutManager - Global component for managing session expiration
 * Place this component in your root App to handle token refresh modals
 * 
 * Usage:
 * <SessionTimeoutManager msalInstance={msalInstance} scopes={scopes} />
 */
export const SessionTimeoutManager = () => {
  const [sessionExpiredModalOpen, setSessionExpiredModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [tokenMsRemaining, setTokenMsRemaining] = useState(null);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const BANNER_SHOW_THRESHOLD_MS = 15 * 60 * 1000;
  const refreshAttemptRef = useRef(0);
  const prevModalOpenRef = useRef(false);
  const bannerTrackedRef = useRef(false);
  const warningTrackedRef = useRef(false);
  const autoRedirectTimeoutRef = useRef(null);
  const proactiveRefreshTimerRef = useRef(null);
  const reAuthPath = '/auth/employee';
  const isAuthRoute = useCallback(() => (
    typeof window !== 'undefined'
      && !!window.location?.pathname
      && window.location.pathname.includes('/auth/')
  ), []);

  const isAuthPage = isAuthRoute();

  const getTokenExpiryMs = useCallback(() => {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      const parsed = readMsalCache(); // picks later-expiring entry from either storage
      if (!parsed) {
        return null;
      }

      const expiresOn = parsed?.expiresOn || parsed?.value?.expiresOn;
      const expiresOnMs = typeof expiresOn === 'number' ? expiresOn : new Date(expiresOn).getTime();
      if (!expiresOnMs || Number.isNaN(expiresOnMs)) {
        return null;
      }

      return expiresOnMs;
    } catch (e) {
      return null;
    }
  }, []);

  const formatRemainingTime = useCallback((remainingMs) => {
    const safeMs = Math.max(0, remainingMs || 0);
    const totalSeconds = Math.floor(safeMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }

    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, []);

  /**
   * Redirect to SSO login page with state preservation
   * Used when silent refresh fails and popup login is not feasible
   */
  const redirectToSSOEmployeePage = useCallback((reason = 'manual') => {
    
    if (typeof window !== 'undefined') {
      if (isAuthRoute()) {
        Auth.clearMsalInteractionLock();
        return;
      }

      const interactionLock = Auth.getMsalInteractionLock();
      if (interactionLock) {
        const lockAgeMs = Date.now() - Number(interactionLock?.createdAt || 0);
        if (lockAgeMs <= 10000) {
          return;
        }
        Auth.clearMsalInteractionLock();
      }

      const isUnrecoverableReason = reason === 'unrecoverable_token_error';
      if (!isUnrecoverableReason) {
        // Store the current URL for post-login redirect
        saveSSOReturnUrl();
        Auth.storeRedirectState();
      } else {
        // Clear any stale return URL so it doesn't affect the fresh login flow
        clearSSOReturnUrl();
        Auth.clearRedirectState();
      }
      
      // Clear session expired flag since we're handling it via redirect
      setSessionExpiredModalOpen(false);
      
      trackEvent('castrol_flow_session_redirect_to_auth', {
        action: 'redirect',
        reason,
        source: 'session_timeout',
        page_path: window.location.pathname
      });

      // Redirect to SSO employee page
      window.location.href = reAuthPath;
    }
  }, [reAuthPath, isAuthRoute]);

  /**
   * Attempt silent token refresh
   */
  const handleRefresh = useCallback(async () => {
    if (isAuthRoute()) {
      return false;
    }
    setIsRefreshing(true);
    refreshAttemptRef.current += 1;

    try {
      const authInstance = window.__AUTH_INSTANCE__;
      const newToken = authInstance?.getMsalAccessToken
        ? await authInstance.getMsalAccessToken()
        : await attemptSilentTokenRefresh();
      
      if (newToken) {
        // Force Apollo client to refetch active queries
        // eslint-disable-next-line no-underscore-dangle
        if (typeof window !== 'undefined' && window.__APOLLO_CLIENT__) {
          try {
            // Refetch all active queries
            // eslint-disable-next-line no-underscore-dangle
            const observableQueries = window.__APOLLO_CLIENT__.getObservableQueries();
            for (const query of observableQueries.values()) {
              try {
                query.refetch?.();
              } catch (e) {
                // Could not refetch query
              }
            }
          } catch (apolloError) {
            // Could not refetch Apollo queries
          }
        }
        
        setSessionExpiredModalOpen(false);
        setIsRefreshing(false);
        refreshAttemptRef.current = 0;
        
        if (typeof window !== 'undefined') {
          const returnUrl = getSSOReturnUrl();
          const currentUrl = window.location.pathname + window.location.search;
          const isErrorPage = currentUrl.includes('/error');
          
          // Clean up return URL from session storage
          clearSSOReturnUrl();

          trackEvent('castrol_flow_session_refresh_success', {
            action: 'refresh',
            attempts: refreshAttemptRef.current,
            target_url: returnUrl || currentUrl,
            source: 'session_timeout_modal'
          });

          if (returnUrl || isErrorPage) {
            // Only navigate when we actually need to go somewhere different:
            const targetUrl = returnUrl || '/content/homepage';
            setTimeout(() => {
              window.location.href = targetUrl;
            }, 200);
          } else {
            window.dispatchEvent(new Event('sessionTokenUpdated'));
          }
        }
        
        return true;
      } 
        setIsRefreshing(false);

        trackEvent('castrol_flow_session_refresh_failure', {
          action: 'refresh',
          reason: 'no_token',
          attempts: refreshAttemptRef.current,
          source: 'session_timeout_modal'
        });
        
        // After 2 failed attempts, redirect to /auth/employee for full SSO login
        if (refreshAttemptRef.current >= 2) {
          // Close modal and redirect
          setSessionExpiredModalOpen(false);
          
          // Use a small delay to ensure UI updates before redirect
          setTimeout(() => {
            redirectToSSOEmployeePage('refresh_failed');
          }, 500);
        }
        
        return false;
      
    } catch (error) {
      setIsRefreshing(false);

      // Unrecoverable errors (invalid_grant / AADSTS700084 SPA refresh token expired,
      // InteractionRequiredAuthError, monitor_window_timeout): silent refresh will never
      // succeed — redirect to SSO immediately without wasting a second attempt.
      const isUnrecoverable =
        error?.isUnrecoverable === true ||
        String(error?.message || '').includes('unrecoverable_token_error') ||
        isMsalTokenExpiredError(error);

      if (isUnrecoverable) {
        setSessionExpiredModalOpen(false);
        trackEvent('castrol_flow_session_refresh_failure', {
          action: 'refresh',
          reason: 'unrecoverable_invalid_grant',
          attempts: refreshAttemptRef.current,
          source: 'session_timeout_modal'
        });
        redirectToSSOEmployeePage('unrecoverable_token_error');
        return false;
      }

      trackEvent('castrol_flow_session_refresh_failure', {
        action: 'refresh',
        reason: 'exception',
        attempts: refreshAttemptRef.current,
        source: 'session_timeout_modal'
      });
      
      // After 2+ failed attempts, redirect to /auth/employee
      if (refreshAttemptRef.current >= 2) {
        setSessionExpiredModalOpen(false);
        
        setTimeout(() => {
          redirectToSSOEmployeePage('refresh_exception');
        }, 500);
      }
      
      return false;
    }
  }, [redirectToSSOEmployeePage, isAuthRoute]);

  /**
   * Handle re-authentication action from session timeout modal.
   * Popup sign-in is intentionally disabled; user is redirected to login page.
   */
  const handleReAuthentication = useCallback(async () => {
    redirectToSSOEmployeePage('user_sign_in_click');
  }, [redirectToSSOEmployeePage]);

  // Register this manager with global state
  React.useEffect(() => {
    // eslint-disable-next-line no-underscore-dangle
    if (typeof window !== 'undefined') {
      // Initialize global state if it doesn't exist
      if (!window.__SESSION_TIMEOUT_MANAGER__) {
        window.__SESSION_TIMEOUT_MANAGER__ = {};
      }
      
      window.__SESSION_TIMEOUT_MANAGER__.updateState = setSessionExpiredModalOpen;
      window.__SESSION_TIMEOUT_MANAGER__.onRefresh = handleRefresh;
      window.__SESSION_TIMEOUT_MANAGER__.onSignIn = handleReAuthentication;
    }
  }, [handleRefresh, handleReAuthentication]);

  // Initialize baseStore change detection on mount
  useEffect(() => {
    initializeBaseStoreChangeDetection();
    
    // Cleanup function for unmount
    return () => {
      if (typeof window !== 'undefined' && window.__baseStoreChangeDetectionCleanup) {
        window.__baseStoreChangeDetectionCleanup();
      }
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleVisibilityChange = () => {
      if (document.hidden) return; // tab going away — nothing to do

      // Check whether the cached token has expired
      let tokenExpired = false;
      try {
        const raw = window.sessionStorage.getItem(MSAL_CACHE) || window.localStorage.getItem(MSAL_CACHE);
        if (raw) {
          const parsed = JSON.parse(raw);
          const rawExp = parsed?.expiresOn || parsed?.value?.expiresOn;
          const exp = typeof rawExp === 'number' ? rawExp : new Date(rawExp).getTime();
          tokenExpired = !rawExp || Number.isNaN(exp) || exp <= Date.now();
        }
      } catch (e) {
        return; // ignore parse errors — let normal flow handle it
      }

      if (!tokenExpired) return;

      const currentPath = window.location?.pathname || '';
      // eslint-disable-next-line no-console
      console.warn('[MSAL][SessionTimeoutManager:visibilitychange] Tab became visible with expired token after idle.', {
        path: currentPath,
        timestamp: Date.now()
      });

      if (currentPath.includes('/auth/asmUser')) {
        // On /auth/asmUser the normal countdown timer is disabled (isAuthPage=true).
        try { window.sessionStorage.removeItem('asmUserSelection.emptyListReloaded'); } catch (_) { /* ignore */ }
        clearMsalStorage(); // clears msal.interaction.lock + auth.msalLastAuthResult + msal.account etc.
        window.location.replace('/auth/employee?relogin=1&reason=idle_wake');
      } else if (!currentPath.includes('/auth/')) {
        // For short idles (access token expired but SPA refresh token still valid)
        const authInst = window.__AUTH_INSTANCE__;
        if (authInst?.getMsalAccessToken) {
          authInst.getMsalAccessToken().then(token => {
            if (token) {
              window.dispatchEvent(new Event('sessionTokenUpdated'));
            } else {
              redirectToSSOEmployeePage('idle_wake');
            }
          }).catch(() => {
            // Unrecoverable (invalid_grant) or other error — redirect to SSO.
            redirectToSSOEmployeePage('idle_wake');
          });
        } else {
          // Auth instance not yet available — redirect immediately.
          redirectToSSOEmployeePage('idle_wake');
        }
      }
      // On /auth/employee: the auth flow itself handles recovery — don't interrupt.
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [redirectToSSOEmployeePage]);

  // Proactive silent token refresh — scheduled at msalTokenRefreshTime (2 min before expiry).
  useEffect(() => {
    if (isAuthPage) {
      return undefined;
    }

    // Falls back to attemptSilentTokenRefresh if Auth instance is not yet available.
    const doProactiveRefresh = () => {
      const authInst = window.__AUTH_INSTANCE__;
      return authInst?.getMsalAccessToken
        ? authInst.getMsalAccessToken()
        : attemptSilentTokenRefresh();
    };

    // Called after every proactive refresh attempt.
    const onRefreshDone = (token) => {
      if (!token) return;
      try {
        const freshParsed = readMsalCache();
        if (!freshParsed) return;
        const freshRefreshTime = Number(
          freshParsed?.msalTokenRefreshTime || freshParsed?.value?.msalTokenRefreshTime || 0
        );
        // If still in the past: no actual refresh happened — let countdown reach 0 naturally,
        // handleRefresh() will make the final attempt before redirecting to SSO.
        if (freshRefreshTime > Date.now()) {
          window.dispatchEvent(new Event('sessionTokenUpdated'));
        }
      } catch (e) { /* ignore cache read errors */ }
    };

    const scheduleProactiveRefresh = () => {
      // Clear any existing timer before rescheduling
      if (proactiveRefreshTimerRef.current) {
        clearTimeout(proactiveRefreshTimerRef.current);
        proactiveRefreshTimerRef.current = null;
      }

      try {
        const parsed = readMsalCache(); // picks later-expiring entry from either storage
        if (!parsed) return;

        const refreshTime = Number(
          parsed?.msalTokenRefreshTime || parsed?.value?.msalTokenRefreshTime || 0
        );
        if (!refreshTime || Number.isNaN(refreshTime)) return;

        const delayMs = refreshTime - Date.now();

        if (delayMs <= 0) {
          // Already past refresh time — attempt immediately (token still valid for ~2 min)
          doProactiveRefresh().then(onRefreshDone).catch(() => {});
        } else {
          // Schedule to fire exactly at msalTokenRefreshTime
          proactiveRefreshTimerRef.current = setTimeout(() => {
            proactiveRefreshTimerRef.current = null;
            doProactiveRefresh().then(onRefreshDone).catch(() => {});
          }, delayMs);
        }
      } catch (e) {
        // Ignore cache read errors
      }
    };

    scheduleProactiveRefresh();

    window.addEventListener('sessionTokenUpdated', scheduleProactiveRefresh);
    window.addEventListener('storage', scheduleProactiveRefresh);
    window.addEventListener('authSessionHeadersReady', scheduleProactiveRefresh);

    return () => {
      if (proactiveRefreshTimerRef.current) {
        clearTimeout(proactiveRefreshTimerRef.current);
        proactiveRefreshTimerRef.current = null;
      }
      window.removeEventListener('sessionTokenUpdated', scheduleProactiveRefresh);
      window.removeEventListener('storage', scheduleProactiveRefresh);
      window.removeEventListener('authSessionHeadersReady', scheduleProactiveRefresh);
    };
  }, [isAuthPage]);

  // Top-right flash banner: show token expiration countdown
  useEffect(() => {
    if (sessionExpiredModalOpen && !prevModalOpenRef.current) {
      trackEvent('castrol_flow_session_timeout_modal_shown', {
        action: 'view',
        source: 'session_timeout_modal',
        page_path: typeof window !== 'undefined' ? window.location.pathname : ''
      });
    }
    prevModalOpenRef.current = sessionExpiredModalOpen;
  }, [sessionExpiredModalOpen]);

  useEffect(() => {
    if (isAuthPage && sessionExpiredModalOpen) {
      setSessionExpiredModalOpen(false);
    }
  }, [isAuthPage, sessionExpiredModalOpen]);

  useEffect(() => {
    if (isAuthPage) {
      setTokenMsRemaining(null);
      return undefined;
    }
    const updateTimer = () => {
      const expiresOnMs = getTokenExpiryMs();
      if (!expiresOnMs) {
        setTokenMsRemaining(null);
        return;
      }

      const remaining = expiresOnMs - Date.now();
      setTokenMsRemaining(remaining > 0 ? remaining : 0);
    };

    updateTimer();
    const timer = setInterval(updateTimer, 1000);
    if (typeof window !== 'undefined') {
      window.addEventListener('sessionTokenUpdated', updateTimer);
      window.addEventListener('storage', updateTimer);
    }

    return () => {
      clearInterval(timer);
      if (typeof window !== 'undefined') {
        window.removeEventListener('sessionTokenUpdated', updateTimer);
        window.removeEventListener('storage', updateTimer);
      }
    };
  }, [getTokenExpiryMs, isAuthPage]);

  useEffect(() => {
    if (tokenMsRemaining !== null && tokenMsRemaining <= BANNER_SHOW_THRESHOLD_MS) {
      if (!bannerTrackedRef.current) {
        bannerTrackedRef.current = true;
        trackEvent('castrol_flow_session_banner_shown', {
          action: 'view',
          remaining_ms: tokenMsRemaining,
          source: 'session_timeout_banner'
        });
      }
    } else if (tokenMsRemaining === null || tokenMsRemaining > BANNER_SHOW_THRESHOLD_MS) {
      bannerTrackedRef.current = false;
    }
  }, [tokenMsRemaining, BANNER_SHOW_THRESHOLD_MS]);

  // Show warning when countdown reaches 0 and auto-redirect
  useEffect(() => {
    let isCancelled = false;

    if (isAuthPage) {
      if (showTimeoutWarning) {
        setShowTimeoutWarning(false);
      }
      return;
    }
    if (tokenMsRemaining !== null && tokenMsRemaining <= 0) {
      if (!warningTrackedRef.current) {
        warningTrackedRef.current = true;

        // Try one silent refresh before warning/redirecting the user.
        handleRefresh().then((refreshed) => {
          if (isCancelled || refreshed) {
            return;
          }

          setShowTimeoutWarning(true);

          trackEvent('castrol_flow_session_timeout_warning_shown', {
            action: 'view',
            source: 'session_timeout_warning',
            page_path: typeof window !== 'undefined' ? window.location.pathname : ''
          });

          // Auto-redirect to login after 5 seconds to give user time to read message
          // Store ref so it can be cancelled if the token is refreshed during this window
          autoRedirectTimeoutRef.current = setTimeout(() => {
            autoRedirectTimeoutRef.current = null;
            redirectToSSOEmployeePage('auto_timeout_redirect');
          }, 5000);
        });
      }
    } else if (tokenMsRemaining === null || tokenMsRemaining > 0) {
      // Token was refreshed externally during the warning window — cancel the pending redirect
      if (autoRedirectTimeoutRef.current) {
        clearTimeout(autoRedirectTimeoutRef.current);
        autoRedirectTimeoutRef.current = null;
      }
      warningTrackedRef.current = false;
      setShowTimeoutWarning(false);
    }

    return () => {
      isCancelled = true;
      if (autoRedirectTimeoutRef.current) {
        clearTimeout(autoRedirectTimeoutRef.current);
        autoRedirectTimeoutRef.current = null;
      }
    };
  }, [tokenMsRemaining, redirectToSSOEmployeePage, isAuthPage, handleRefresh]);

  return (
    <>
      {showTimeoutWarning && !isAuthPage && (
        <div
          style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10001,
            backgroundColor: '#ffffff',
            padding: '32px',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.25)',
            maxWidth: '500px',
            textAlign: 'center'
          }}
          role="alert"
          aria-live="assertive"
        >
          <div
            style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}
            aria-hidden="true"
          >
            ⏱️
          </div>
          <h2
            style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: 600,
              color: '#222222'
            }}
          >
            Session Timeout
          </h2>
          <p
            style={{
              margin: '0',
              fontSize: '14px',
              color: '#666666',
              lineHeight: '1.6'
            }}
          >
            Please relogin your application else your application will be re-login automatically
          </p>
        </div>
      )}

      {showTimeoutWarning && !isAuthPage && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            left: '0',
            right: '0',
            bottom: '0',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 10000,
            backdropFilter: 'blur(2px)'
          }}
          aria-hidden="true"
        />
      )}

      {tokenMsRemaining !== null && tokenMsRemaining <= BANNER_SHOW_THRESHOLD_MS && !showTimeoutWarning && !isAuthPage && (
        <div
          style={{
            position: 'fixed',
            top: '0',
            right: '0',
            zIndex: 10000,
            minHeight: '15px',
            padding: '1px 14px',
            borderRadius: '0 0 0 8px',
            backgroundColor: '#009343',
            border: 'none',
            color: '#ffffff',
            fontSize: '12px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.12)'
          }}
          role="status"
          aria-live="polite"
        >
          Time left before session-timeout popup: {formatRemainingTime(tokenMsRemaining)}
        </div>
      )}

      <SessionTimeoutModal
        isOpen={sessionExpiredModalOpen && !isAuthPage}
        onRefresh={handleRefresh}
        onSignIn={handleReAuthentication}
        isLoading={isRefreshing}
        title="Session Expired"
        message="Sorry!!, your session has been expired please log in again"
        isRefreshOptionVisible
        reAuthPath={reAuthPath}
      />
    </>
  );
};

SessionTimeoutManager.propTypes = {
  msalInstance: PropTypes.shape({
    loginPopup: PropTypes.func,
    enableAccountStorageEvents: PropTypes.func,
    setActiveAccount: PropTypes.func
  })
};

export default SessionTimeoutManager;
